/**
 * C4 Model Node Component
 * Renders a C4 element styled by `elementType`:
 *   - person                       : actor head + rounded body, name + description.
 *   - system / container / component : rounded box with an element-type chip,
 *     name, optional [technology] line, and description.
 * The `external` flag greys the element (outside the system-in-focus boundary),
 * matching Structurizr/C4 convention. Relationships are ordinary labelled edges.
 */

import { memo, useEffect } from 'react'
import { NodeProps, NodeResizer, useUpdateNodeInternals } from '@xyflow/react'
import type { C4Content } from '../../types/schema'
import { NodeConnectionHandles } from './NodeConnectionHandles'

interface C4NodeData {
  label: string
  c4Content?: C4Content
  style?: { bgColor?: string; borderColor?: string; textColor?: string; fontFamily?: string }
}

// Element-type palette (internal vs external tints).
const PALETTE: Record<string, { bg: string; border: string; chip: string }> = {
  person: { bg: '#1168bd', border: '#0d4d8c', chip: 'Person' },
  system: { bg: '#1168bd', border: '#0d4d8c', chip: 'Software System' },
  container: { bg: '#438dd5', border: '#2e6db0', chip: 'Container' },
  component: { bg: '#85bbf0', border: '#5a93cc', chip: 'Component' },
}
const EXTERNAL = { bg: '#999999', border: '#6b6b6b' }

function C4Node({ id, data, selected, width, height }: NodeProps) {
  const nodeData = (data as unknown) as C4NodeData
  const c4 = nodeData.c4Content
  const updateNodeInternals = useUpdateNodeInternals()
  useEffect(() => { updateNodeInternals(id) }, [id, updateNodeInternals])

  const elementType = c4?.elementType ?? 'system'
  const pal = PALETTE[elementType] ?? PALETTE.system
  const bg = c4?.external ? EXTERNAL.bg : ((nodeData as any).bgColor || nodeData.style?.bgColor || pal.bg)
  const border = c4?.external ? EXTERNAL.border : ((nodeData as any).borderColor || nodeData.style?.borderColor || pal.border)
  const fontFamily = nodeData.style?.fontFamily || 'Inter, system-ui, sans-serif'

  const nodeWidth = width || 190
  const nodeHeight = height || (elementType === 'person' ? 150 : 120)
  const isPerson = elementType === 'person'

  return (
    <>
      {selected && <NodeResizer minWidth={140} minHeight={90} />}
      <div style={{ width: nodeWidth, height: nodeHeight, position: 'relative', fontFamily, color: '#ffffff' }}>
        {isPerson && (
          // Actor head
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: bg, border: `2px solid ${border}`, margin: '0 auto -10px', position: 'relative', zIndex: 1 }} />
        )}
        <div
          style={{
            width: '100%',
            height: isPerson ? nodeHeight - 34 : nodeHeight,
            background: bg,
            border: `2px solid ${border}`,
            borderRadius: isPerson ? 24 : 8,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '10px 12px',
            overflow: 'hidden',
          }}
        >
          <div style={{ fontSize: 10, opacity: 0.85, marginBottom: 2 }}>
            [{c4?.external ? 'External ' : ''}{pal.chip}]
          </div>
          <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }} title={c4?.name}>
            {c4?.name ?? nodeData.label}
          </div>
          {c4?.technology && (
            <div style={{ fontSize: 10, fontStyle: 'italic', opacity: 0.9, marginTop: 2 }} title={c4.technology}>
              [{c4.technology}]
            </div>
          )}
          {c4?.description && (
            <div style={{ fontSize: 11, opacity: 0.92, marginTop: 6, lineHeight: 1.3 }} title={c4.description}>
              {c4.description}
            </div>
          )}
        </div>
        <NodeConnectionHandles />
      </div>
    </>
  )
}

export default memo(C4Node)
